import React from 'react';
import { Controller, Control, FieldErrors } from 'react-hook-form';
import { FieldTypeEnum } from '../../FieldBank/_types';
import { BaseTextInput, BaseNumberInput, BaseBooleanInput, BaseDateInput, BaseTimeInput } from './index';
import dayjs from 'dayjs';

export interface FormFieldInputProps {
  fieldType: FieldTypeEnum;
  control: Control<any>;
  errors: FieldErrors<any>;
  name: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  // Additional props for specific field types
  maxLength?: number;
  min?: number;
  max?: number;
  showTimezone?: boolean;
}

const FormFieldInput: React.FC<FormFieldInputProps> = ({
  fieldType,
  control,
  errors,
  name,
  label,
  placeholder,
  required = false,
  disabled = false,
  maxLength,
  min,
  max,
  showTimezone = true,
}) => {
  const fieldError = errors[name];
  const errorMessage = fieldError?.message as string;

  const commonProps = {
    label,
    required,
    disabled,
    error: errorMessage,
    size: 'medium' as const,
    fullWidth: true,
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        switch (fieldType) {
          case 'Text':
          case 'Lookup':
            return (
              <BaseTextInput
                {...commonProps}
                value={field.value || ''}
                onChange={field.onChange}
                placeholder={placeholder || 'Enter text value'}
                maxLength={maxLength}
              />
            );

          case 'Number':
            return (
              <BaseNumberInput
                {...commonProps}
                value={field.value || ''}
                onChange={field.onChange}
                placeholder={placeholder || 'Enter number'}
                min={min}
                max={max}
              />
            );

          case 'Boolean':
            return (
              <BaseBooleanInput
                {...commonProps}
                value={field.value !== null && field.value !== undefined && field.value !== '' ? field.value : 'false'}
                onChange={(value) => {
                  // Ensure we always store a boolean string value, defaulting to 'false'
                  field.onChange(value);
                }}
                label={label || 'Default value'}
              />
            );

          case 'Date':
            return (
              <BaseDateInput
                {...commonProps}
                value={field.value ? (dayjs.isDayjs(field.value) ? field.value.format('YYYY-MM-DD') : field.value) : ''}
                onChange={(value) => {
                  // Convert string to Dayjs and store in form (same as original DateField logic)
                  if (value && value !== '') {
                    const date = dayjs(value);
                    field.onChange(date);
                  } else {
                    field.onChange(null);
                  }
                }}
                label={label || 'Default date value'}
              />
            );

          case 'Time':
            return (
              <BaseTimeInput
                {...commonProps}
                value={field.value}
                onChange={(value) => {
                  // Store Dayjs objects directly like the original TimeField
                  field.onChange(value);
                }}
                label={label || 'Default time value'}
                showTimezone={showTimezone}
              />
            );

          case 'SingleSelect':
            // SingleSelect will be handled separately due to its complexity
            return (
              <BaseTextInput
                {...commonProps}
                value={field.value || ''}
                onChange={field.onChange}
                placeholder={placeholder || 'Select default option'}
                disabled={true}
                helperText='SingleSelect inputs require special handling'
              />
            );

          default:
            console.warn('Unknown field type:', fieldType, 'defaulting to Text input');
            return (
              <BaseTextInput
                {...commonProps}
                value={field.value || ''}
                onChange={field.onChange}
                placeholder={placeholder || 'Enter default value'}
              />
            );
        }
      }}
    />
  );
};

export default FormFieldInput;
